






































































































































































































import Vue, { PropType } from 'vue';
import store from '@/store';

import { WebRequestStatus } from '@/models';
import { SinglePointPacket, Objective, Badge, Outcome } from '@/models/packet';
import * as Icons from '@/icons';
import { idGenerator } from '@/utils';

import CogsDefinition from '@/components/widgets/CogsDefinition.vue';
import HideableGoal from '@/components/HideableGoal.vue';
import Icon from '@/components/Icon.vue';
import PersistableItem from '@/components/PersistableItem.vue';
import SinglePointBasicInfoEditor from '@/components/SinglePointBasicInfoEditor.vue';
import SinglePointGoalEditor from '@/components/SinglePointGoalEditor.vue';
import SinglePointGoalInput from '@/components/SinglePointGoalInput.vue';

interface EditableBadge {
  badgeId: number;
  objectiveId: number;
  outcomeId: number;
  description: string;
}

interface EditableIndicator {
  outcomeId: number;
  value: string;
  iconData: Icons.IconData;
  color: string;
  sortOrder: number;
}

type GoalInfo = [Badge, Outcome, WebRequestStatus];

enum Mode {
  EDIT,
  VIEW
}

const emptyEditableBadge = {
  badgeId: -1,
  description: '',
  objectiveId: -1,
  outcomeId: -1
};

const emptyEditableIndicator = {
  outcomeId: -1,
  value: '',
  iconData: Icons.default.lookup('MaterialDesignIconFont', 0xf52d),
  color: '#BEEF99',
  sortOrder: -1
};

export default Vue.extend({
  components: {
    CogsDefinition,
    HideableGoal,
    Icon,
    PersistableItem,
    SinglePointBasicInfoEditor,
    SinglePointGoalEditor,
    SinglePointGoalInput
  },

  props: {
    packet: {
      type: Object as PropType<SinglePointPacket>,
      required: true
    },
    badges: {
      type: Array as PropType<[Badge, WebRequestStatus][]>,
      required: true
    },
    createStatus: {
      type: Number as PropType<WebRequestStatus>,
      required: false,
      default: WebRequestStatus.READY
    },
    basicInfoStatus: {
      type: Number as PropType<WebRequestStatus>,
      required: false,
      default: WebRequestStatus.READY
    },
    alwaysEditing: {
      type: Boolean,
      default: false
    },
    disableEditing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editableBadge: emptyEditableBadge,
      editableIndicator: emptyEditableIndicator,
      icons: Icons.default.icons,
      isEditingGoal: false,
      isCreatingGoal: false,
      defaultIconData: Icons.default.lookup('MaterialDesignIconFont', 0xf52d),
      defaultDescription: 'This cog is amazing because...',
      newCogWarning:
        'New Cogs are wonderful things, but take note: once you or one of your learners select ' +
        'this cog for a post, you will not be able to delete it.',
      defaultName: 'New Cog',
      defaultColor: '#BEEF99',
      tempCreateId: idGenerator.next(),
      mode: this.alwaysEditing ? Mode.EDIT : Mode.VIEW
    };
  },

  computed: {
    objective(): Objective {
      return this.packet.objectives[0];
    },

    goals(): GoalInfo[] {
      const goals = this.badges.flatMap(([b, status]) => {
        const indicator = this.packet.outcomes.find(
          out => out.outcomeId === b.outcomeId
        );

        if (!indicator) {
          const message = 'Missing indicator for badge';
          console.error({ badge: b, packet: this.packet, message });
          return [];
          // throw new Error(message);
        }

        return [[b, indicator, status]] as GoalInfo[];
      });

      goals.sort(
        ([, indicatorA], [, indicatorB]) =>
          indicatorA.sortOrder - indicatorB.sortOrder
      );

      return goals;
    },

    showEditOptions(): boolean {
      return this.mode === Mode.EDIT;
    },

    isInitialSetup(): boolean {
      return this.packet.outcomes.length === 0;
    },

    canEdit(): boolean {
      return this.packet.canEdit;
    },

    cogsSetName(): string {
      return this.packet.name || 'Name this set of COGS *';
    }
  },

  methods: {
    editGoalStart([badge, indicator]: GoalInfo) {
      if (this.editableBadge.badgeId != badge.badgeId) {
        this.editableBadge = {
          ...badge
        };

        this.editableIndicator = {
          ...indicator
        };
      }

      if (this.packet.packetId > 0) {
        store.dispatch.usage.getOutcomeUsage({
          packetId: this.packet.packetId,
          outcomeId: indicator.outcomeId
        });
      } else {
        store.dispatch.usage.zero();
      }

      this.isEditingGoal = true;
    },

    editGoalClose() {
      this.isEditingGoal = false;
    },

    createGoalStart() {
      this.isCreatingGoal = true;
    },

    createGoal(goal: {
      name: string;
      description: string;
      color: string;
      iconData: Icons.IconData;
    }) {
      this.createGoalClose();
      this.$emit('create-goal', goal);
    },

    createGoalClose() {
      this.isCreatingGoal = false;
    },

    saveGoal(goal: {
      name: string;
      description: string;
      color: string;
      iconData: Icons.IconData;
    }) {
      const badgeIsChanged =
        this.editableBadge.description !== goal.description;

      const outcomeIsChanged =
        this.editableIndicator.value !== goal.name ||
        this.editableIndicator.color !== goal.color ||
        this.editableIndicator.iconData.codePoint !== goal.iconData.codePoint;

      const updateEvent = {
        badge: badgeIsChanged
          ? { ...this.editableBadge, description: goal.description }
          : null,
        outcome: outcomeIsChanged
          ? {
              ...this.editableIndicator,
              value: goal.name,
              color: goal.color,
              iconData: goal.iconData
            }
          : null
      };

      this.$emit('update-goal', updateEvent);

      this.editableBadge = emptyEditableBadge;
      this.editableIndicator = emptyEditableIndicator;

      this.editGoalClose();
    },

    // Called from inside the edit goal dialog.
    // Should update the goal as hidden and then close the dialog
    deactivateGoal() {
      this.$emit('update-goal', {
        outcome: {
          ...this.editableIndicator,
          isActive: false
        }
      });

      this.editableBadge = emptyEditableBadge;
      this.editableIndicator = emptyEditableIndicator;

      this.editGoalClose();
    },

    // Called from the packet edit screen; _not_ called from inside the edit dialog
    // Button should replace the edit button if the goal is hidden.
    //
    // Placing this outside the dialog sidesteps the need to worry about whether hitting
    // "show" might lose some changes to the name, description or icon.
    activateGoal(outcome: Outcome) {
      this.$emit('update-goal', {
        outcome: {
          ...outcome,
          isActive: true
        }
      });
    },

    deleteGoal(outcome: Outcome) {
      // Deleting the outcome cascade-deletes the badge.
      //
      // Deleting fails (and should not be enabled) when there exists at least
      // one evaluation of the outcome.
      this.$emit('delete-goal', outcome);

      this.editableBadge = emptyEditableBadge;
      this.editableIndicator = emptyEditableIndicator;
      this.editGoalClose();
    },

    updateObjective(objective: Objective) {
      this.$emit('update-objective', objective);
    },

    turnOnEditing() {
      this.mode = Mode.EDIT;
    },

    turnOffEditing() {
      this.mode = Mode.VIEW;
    }
  }
});
